import React, { useState, useEffect } from "react";
import { Cookie, X, Shield, BarChart3, Megaphone } from "lucide-react";
import { useConsent } from "../hooks/useConsent";

const CATEGORIES = [
  {
    key: "essential",
    icon: Shield,
    title: "Essential",
    desc: "Required for the site to function — page navigation, theme preference, security, anti-spam. Cannot be disabled.",
    locked: true,
  },
  {
    key: "analytics",
    icon: BarChart3,
    title: "Analytics",
    desc: "Helps us understand how visitors use the site (pages, sessions, performance) so we can improve. Includes Google Analytics 4 and Microsoft Clarity heatmaps when enabled.",
  },
  {
    key: "marketing",
    icon: Megaphone,
    title: "Marketing",
    desc: "Used to deliver more relevant content, measure campaign performance, and reach you on other platforms (e.g., LinkedIn pixel for retargeting).",
  },
];

export default function CookieConsent() {
  const { decided, categories, acceptAll, rejectNonEssential, save } = useConsent();
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState(categories);

  // Keep draft in sync with stored categories whenever modal opens
  useEffect(() => {
    if (showModal) setDraft(categories);
  }, [showModal, categories]);

  // Listen for footer "Cookie preferences" link click
  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener("capexara:open-cookie-prefs", handler);
    return () => window.removeEventListener("capexara:open-cookie-prefs", handler);
  }, []);

  const toggle = (key) => {
    if (key === "essential") return;
    setDraft((d) => ({ ...d, [key]: !d[key] }));
  };

  const savePrefs = () => {
    save(draft);
    setShowModal(false);
  };

  return (
    <>
      {/* First-visit bottom banner */}
      {!decided && !showModal && (
        <div className="cookie-banner" role="dialog" aria-label="Cookie preferences" data-testid="cookie-banner">
          <div className="cookie-banner-inner">
            <div className="cookie-banner-icon" aria-hidden="true"><Cookie size={20} /></div>
            <div className="cookie-banner-body">
              <div className="cookie-banner-title">Your privacy, your call.</div>
              <div className="cookie-banner-text">
                We use cookies to make Capexara AI work, measure how it's used, and tailor what you see.
                You can accept all, reject non-essential, or pick what you're comfortable with.
                {" "}<a href="#cookie-prefs" onClick={(e) => { e.preventDefault(); setShowModal(true); }}>Read about each category.</a>
              </div>
            </div>
            <div className="cookie-banner-actions">
              <button type="button" className="btn btn-ghost cookie-btn-sm" onClick={() => setShowModal(true)} data-testid="cookie-customize-btn">
                Customize
              </button>
              <button type="button" className="btn btn-ghost cookie-btn-sm" onClick={rejectNonEssential} data-testid="cookie-reject-btn">
                Reject non-essential
              </button>
              <button type="button" className="btn btn-primary cookie-btn-sm" onClick={acceptAll} data-testid="cookie-accept-all-btn">
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customize modal */}
      {showModal && (
        <div className="cookie-modal-backdrop" role="dialog" aria-modal="true" aria-label="Cookie preferences" data-testid="cookie-modal" onClick={() => setShowModal(false)}>
          <div className="cookie-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cookie-modal-head">
              <div>
                <div className="cookie-modal-eyebrow">◉ Privacy</div>
                <h2 className="cookie-modal-title">Cookie preferences</h2>
              </div>
              <button type="button" className="cookie-modal-close" onClick={() => setShowModal(false)} aria-label="Close" data-testid="cookie-modal-close-btn">
                <X size={18} />
              </button>
            </div>
            <p className="cookie-modal-intro">
              Choose which cookie categories you allow on capexara.com. Essential cookies are always on so the site works.
              Your choice is stored on this browser and you can change it any time from the footer or our{" "}
              <a href="/cookies">Cookie Notice</a>. See our <a href="/privacy">Privacy Policy</a> for the broader picture.
            </p>

            <div className="cookie-cat-list">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const enabled = c.locked ? true : !!draft[c.key];
                return (
                  <div key={c.key} className={"cookie-cat " + (enabled ? "on" : "")} data-testid={`cookie-cat-${c.key}`}>
                    <div className="cookie-cat-icon" aria-hidden="true"><Icon size={16} /></div>
                    <div className="cookie-cat-body">
                      <div className="cookie-cat-title">
                        {c.title}
                        {c.locked && <span className="cookie-cat-locked">Always on</span>}
                      </div>
                      <div className="cookie-cat-desc">{c.desc}</div>
                    </div>
                    <button
                      type="button"
                      className={"cookie-toggle " + (enabled ? "on" : "")}
                      onClick={() => toggle(c.key)}
                      role="switch"
                      aria-checked={enabled}
                      aria-label={`${c.title} cookies`}
                      disabled={c.locked}
                      data-testid={`cookie-toggle-${c.key}`}
                    >
                      <span className="cookie-toggle-knob" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="cookie-modal-foot">
              <button type="button" className="btn btn-ghost cookie-btn-sm" onClick={() => { rejectNonEssential(); setShowModal(false); }} data-testid="cookie-modal-reject-btn">
                Reject non-essential
              </button>
              <button type="button" className="btn btn-ghost cookie-btn-sm" onClick={() => { acceptAll(); setShowModal(false); }} data-testid="cookie-modal-accept-all-btn">
                Accept all
              </button>
              <button type="button" className="btn btn-primary cookie-btn-sm" onClick={savePrefs} data-testid="cookie-modal-save-btn">
                Save my choices
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
